import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { runWhenPageReady } from '../utils/PageLifecycle';

gsap.registerPlugin(ScrollTrigger);

/**
 * FadeInOnScroll - Simple fade and move up animation on scroll
 * 
 * Usage:
 * <div data-fade-in data-fade-delay="0.2">Content</div>
 * 
 * For opacity-only fade (no vertical movement):
 * <p data-fade-in data-fade-opacity-only data-fade-delay="0.2">Text</p>
 * 
 * Optional attributes:
 * - data-fade-delay: Delay before animation starts (seconds)
 * - data-fade-distance: Distance to move (pixels, default 30)
 * - data-fade-opacity-only: If present, only fades opacity (no vertical movement)
 */
class FadeInOnScroll {
  constructor(element) {
    this.element = element;
    this.delay = parseFloat(element.dataset.fadeDelay) || 0;
    // Allow 0 as a valid distance value (use default 30 only if attribute is missing)
    this.distance = element.dataset.fadeDistance !== undefined 
      ? parseFloat(element.dataset.fadeDistance) 
      : 30;
    this.trigger = element.dataset.fadeTrigger || 'top bottom-=50';
    this.opacityOnly = element.hasAttribute('data-fade-opacity-only');
    
    // Detect mobile for faster animations
    this.isMobile = window.innerWidth <= 768;
    
    this.init();
  }
  
  init() {
    // Mobile: shorter distance and faster duration
    const distance = this.isMobile ? 15 : this.distance;
    const duration = this.isMobile ? 0.4 : 0.8;
    const delay = this.isMobile ? this.delay * 0.5 : this.delay; // Half delays on mobile
    
    // Set initial state
    const initialState = {
      opacity: 0
    };
    
    // Only add vertical movement if not opacity-only
    if (!this.opacityOnly) {
      initialState.y = distance;
    }
    
    gsap.set(this.element, initialState);
    
    // Animate in on scroll
    const animationProps = {
      opacity: 1,
      duration: duration,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: this.element,
        start: this.trigger,
        toggleActions: 'play none none none',
        // markers: true // Uncomment for debugging
      },
      delay: delay
    };
    
    // Only add vertical movement if not opacity-only
    if (!this.opacityOnly) {
      animationProps.y = 0;
    }
    
    gsap.to(this.element, animationProps);
  }
}

/**
 * Auto-initialize all elements with data-fade-in
 * Excludes carousel slides which handle their own initialization
 */
function initFadeInOnScroll() {
  const elements = document.querySelectorAll('[data-fade-in]');
  console.log(`FadeInOnScroll: Found ${elements.length} elements with data-fade-in`);
  
  // Group elements by parent container for batch animations
  const grouped = new Map();
  
  elements.forEach(element => {
    // Skip elements inside carousel containers (they handle their own init)
    const isInsideCarousel = element.closest('.offers-carousel, .room-carousel, [data-carousel]');
    if (isInsideCarousel) {
      console.log('Skipping carousel element:', element.className);
      return;
    }
    
    // Check if element is part of a grid that should cascade
    const gridParent = element.closest('.team-section__grid, .stats-feature__grid');
    
    if (gridParent) {
      if (!grouped.has(gridParent)) {
        grouped.set(gridParent, []);
      }
      grouped.get(gridParent).push(element);
    } else {
      // Individual element - init normally
      console.log('Initializing FadeInOnScroll for:', element.className || element);
      new FadeInOnScroll(element);
    }
  });
  
  // Handle grouped elements with batch ScrollTrigger
  grouped.forEach((groupElements, parent) => {
    console.log(`Initializing batch animation for ${groupElements.length} elements in grid`);
    initBatchFadeIn(groupElements, parent);
  });
}

/**
 * Initialize batch fade-in animation for grid elements
 */
function initBatchFadeIn(elements, container) {
  // Detect mobile for faster animations
  const isMobile = window.innerWidth <= 768;
  const distance = isMobile ? 20 : 40;
  const duration = isMobile ? 0.4 : 0.8;
  const stagger = isMobile ? 0.08 : 0.15; // Faster stagger on mobile
  
  // Set initial state for all elements
  gsap.set(elements, {
    opacity: 0,
    y: distance
  });
  
  // Create staggered animation on scroll
  ScrollTrigger.batch(elements, {
    onEnter: batch => {
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        duration: duration,
        stagger: stagger,
        ease: 'power2.out'
      });
    },
    start: 'top 85%',
    once: true
  });
}

runWhenPageReady(initFadeInOnScroll);

// Refresh ScrollTrigger when window fully loads (images, fonts, etc.)
window.addEventListener('load', () => {
  ScrollTrigger.refresh();
});

export default FadeInOnScroll;
